import { Image } from 'expo-image';
import { useRouter, usePathname } from 'expo-router';
import { cssInterop } from 'nativewind';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity, Image as RNImage } from 'react-native';

import { Icon, CheckIcon, ChevronDownIcon } from '@/components/gluestack/icon';
import { SlideMenu, SlideMenuItem, SlideMenuItemLabel } from '@/components/ui/slide-menu';
import { FEED_ROUTES } from '@/constants/routes';
import { useDefaultFeed } from '@/hooks/useDefaultFeed';
import { HStack } from '~/components/gluestack/hstack';
import { useAuth } from '~/components/providers/AuthProvider';
import { communities, CommunityCode } from '~/constants/communities';
import { useCommunity } from '~/hooks/useCommunity';
import { useCommunitySwitch } from '~/hooks/useCommunitySwitch';
import {
  triggerPageNavigationReset,
  triggerPageRefresh,
  clearStoredScrollPosition,
} from '~/hooks/useInfiniteListUtils';

cssInterop(Image, { className: 'style' });

export function CommunitySelectorHeader() {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const { getCredentials } = useAuth();
  const currentCommunity = useCommunity();
  const [isSwitching, setIsSwitching] = useState(false);
  const { defaultFeed } = useDefaultFeed();
  const { performCommunitySwitch } = useCommunitySwitch();

  const handleLogoPress = () => {
    const isOnFeedsPage = pathname.startsWith('/feeds/');
    const feedRoute = FEED_ROUTES[defaultFeed];
    const pageKey = `/feeds/${defaultFeed}`;

    // Clear scroll position
    clearStoredScrollPosition(pageKey);

    if (isOnFeedsPage) {
      // If already on a feeds page, trigger refresh
      triggerPageRefresh(pageKey);
    } else {
      // Large-screen navigation can reveal an already-mounted feed list,
      // so reset it before switching back to the feeds tab family.
      triggerPageNavigationReset(pageKey);
    }

    router.push(feedRoute);
  };

  const logoSource = currentCommunity?.logo;
  const aspectRatio = useMemo(() => {
    if (Platform.OS === 'web' || !logoSource) return undefined;
    const source = RNImage.resolveAssetSource(logoSource);
    return source ? source.width / source.height : undefined;
  }, [logoSource]);

  // Ticket says "This is a native-only feature. Not for web use."
  // However, providing a fallback for web or single community ensures the component is safe to use.
  const renderLogo = () => (
    <Image
      className={`h-8 ${!aspectRatio ? 'w-40' : ''}`}
      style={aspectRatio ? { aspectRatio } : undefined}
      source={currentCommunity?.logo || ''}
      contentPosition="left"
      contentFit="contain"
      alt={currentCommunity?.name || ''}
      priority="high"
      recyclingKey={currentCommunity?.code}
    />
  );

  if (Platform.OS === 'web') {
    return (
      <TouchableOpacity onPress={handleLogoPress} activeOpacity={0.8}>
        {renderLogo()}
      </TouchableOpacity>
    );
  }

  const myCommunities = Object.values(communities).filter((c) => {
    const creds = getCredentials(c.code as CommunityCode);
    return !!creds;
  });

  if (myCommunities.length <= 1) {
    return renderLogo();
  }

  const handleCommunitySwitch = async (code: CommunityCode) => {
    if (code === currentCommunity?.code || isSwitching) return;

    setIsSwitching(true);
    try {
      // Theme flip, SignalR reconnect, auth switch and the navigation reset
      // all run behind the full-screen overlay (see useCommunitySwitch).
      await performCommunitySwitch(code, FEED_ROUTES[defaultFeed]);
    } catch (error) {
      console.error('Failed to switch community:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <SlideMenu
      placement="bottom left"
      offset={5}
      trigger={({ ...triggerProps }) => (
        <TouchableOpacity {...triggerProps} activeOpacity={0.8}>
          <HStack className="items-center" space="sm">
            {renderLogo()}
            <Icon as={ChevronDownIcon} size="md" className="text-white" />
          </HStack>
        </TouchableOpacity>
      )}>
      <SlideMenuItem key="my-communities-header" disabled>
        <SlideMenuItemLabel size="sm" className="font-bold uppercase text-typography-500">
          {t('communities.myCommunities')}
        </SlideMenuItemLabel>
      </SlideMenuItem>
      {myCommunities.map((community) => (
        <SlideMenuItem
          key={community.code}
          onPress={() => handleCommunitySwitch(community.code as CommunityCode)}>
          <HStack space="md" className="flex-1 items-center">
            <Image
              source={community.icon}
              className="h-8 w-8 rounded-full"
              contentFit="contain"
              alt={community.name}
            />
            <SlideMenuItemLabel size="md" className="text-typography-900">
              {community.name}
            </SlideMenuItemLabel>
            {community.code === currentCommunity?.code && (
              <Icon as={CheckIcon} size="sm" className="text-typography-900" />
            )}
          </HStack>
        </SlideMenuItem>
      ))}
    </SlideMenu>
  );
}
